import {
  ENDPOINTS,
  generateOptions,
  RelatedClientType,
  Status,
  WebTable
} from '@constants';
import AgTable from '@containers/AgTable';
import { Prototype } from '@core';
import { useAgGlobalTable, useFetchDashboardByClient } from '@hooks';
import useAgInfTable from '@hooks/core/useAgInfTable';
import { IUseAgInfTableProps } from '@hooks-dto';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { isEmpty, isNull, map } from 'lodash';
import React, { memo, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import trans from 'translation';
import { KContainer, KGrid, KInput, KColors, KChip, KLabel } from 'uikit';
import Loading from 'uikit/uikit/Loading';

enum CalculatedMethods {
  NumOfInvoices = 'NUMBER_OF_INVOICES',
  InvoiceAmount = 'INVOICE_AMOUNT'
}
enum ClientType {
  AccountsReceivable = 'ACCOUNTS_RECEIVABLE',
  ConsignorConsignee = 'CONSIGNOR_CONSIGNEE'
}

const Client = () => {
  const [clientType, setClientType] = useState(null);
  const [clients, setClients] = useState<any[]>([]);
  const [calculatedBy, setCalculatedBy] = useState(null);

  const clientIds = useMemo(() => map(clients, 'id'), [clients]);

  const { data: byClientData, isLoading } = useFetchDashboardByClient({
    clientIds,
    relatedClientType: clientType,
    calculatedBy
  });

  const clientStatisticData = useMemo(() => {
    const labels = map(byClientData?.statistic, 'client.code');
    return {
      labels,
      datasets: [
        {
          label: trans('paid'),
          data: map(byClientData?.statistic, 'paid'),
          backgroundColor: '#0263FF'
        },
        {
          label: trans('overdue'),
          data: map(byClientData?.statistic, 'overdue'),
          backgroundColor: '#FF7723'
        },
        {
          label: trans('balance_due'),
          data: map(byClientData?.statistic, 'balanceDue'),
          backgroundColor: '#0B9694'
        }
      ]
    };
  }, [byClientData]);

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            padding: 20,
            color: KColors.black
          },
          display: true
        },
        tooltip: {
          mode: 'index',
          intersect: true
        },
        datalabels: {
          color: 'white',
          anchor: 'center',
          align: 'center',
          formatter: value => value
        }
      },
      scales: {
        x: {
          ticks: {
            font: {
              size: 12,
              weight: 'normal'
            },
            color: KColors.black
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            font: {
              size: 12,
              weight: 'normal'
            },
            color: KColors.black
          }
        }
      }
    }),
    []
  );

  const colDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        headerName: trans('invoice_number'),
        field: 'code'
      },
      {
        headerName: trans('total_amount'),
        field: 'totalAmount'
      },
      {
        headerName: trans('due_date'),
        field: 'dueDate'
      },
      {
        headerName: trans('email'),
        field: 'transportJob.consignClient.defaultContact.email',
        valueGetter: ({ data }: ValueGetterParams) => {
          const value =
            clientType === ClientType.AccountsReceivable
              ? data?.transportJob?.relatedPartyArClient?.relatedClient
                  ?.defaultContact?.email
              : data?.transportJob.consignClient.defaultContact.email;
          return value;
        }
      }
    ],
    [clientType]
  );

  const tableProps = useMemo((): IUseAgInfTableProps<any> => {
    return {
      name: WebTable.DASHBOARD_BY_CLIENT,
      apiURL: ENDPOINTS.transportJob('invoice'),
      colDefs,
      isInitFetch: !!(clientType && clientIds.length > 0),
      otherParams: {
        clientIdIn: clientIds,
        relatedClientType: clientType,
        isAR: true
      }
    };
  }, [clientIds, clientType, colDefs]);

  const table = useAgInfTable(tableProps);

  useAgGlobalTable(table);

  return (
    <KContainer.Card
      marginT={0}
      dp="flex"
      header={{
        title: trans('by_client'),
        typo: 'TextMdBold'
      }}
    >
      <KGrid.Container spacing={2} alignItems="stretch">
        <KGrid.Item xs={3}>
          <KInput.TextField
            value={clientType}
            onChange={(v: any) => {
              setClients([]);
              setClientType(v.target.value);
            }}
            label={trans('client_type')}
            options={generateOptions(ClientType)}
          />
        </KGrid.Item>
        <KGrid.Item xs={6}>
          <KInput.Autocomplete
            label={trans('filter_by_client')}
            value={clients}
            onChange={(v: any) => {
              setClients(v);
            }}
            multiple
            apiURL={ENDPOINTS.client()}
            apiParams={{
              status: Status.Active,
              typeIn:
                clientType === RelatedClientType.ConsignorConsignee
                  ? [RelatedClientType.Consignor, RelatedClientType.Consignee]
                  : RelatedClientType.AccountsReceivable
            }}
            renderTags={(tagValue, getTagProps) => {
              return tagValue?.map((i: any, index) => {
                const isActive = i?.status === Status.Active;
                return (
                  <KChip
                    key={i.id}
                    {...getTagProps({ index })}
                    label={i.name}
                    isLink
                    onPress={() => {
                      window.open(`/admin/clients/${i.id}`);
                    }}
                    color={isActive ? undefined : 'secondary'}
                  />
                );
              });
            }}
            disabled={isNull(clientType)}
          />
        </KGrid.Item>
        <KGrid.Item xs={3}>
          <KInput.TextField
            value={calculatedBy}
            onChange={(v: any) => {
              setCalculatedBy(v?.target?.value);
            }}
            label={trans('calculated_by')}
            disabled={isEmpty(clients)}
            options={generateOptions(CalculatedMethods)}
            placeholder={trans('search')}
          />
        </KGrid.Item>
      </KGrid.Container>

      <KGrid.Container
        spacing={2}
        alignItems="stretch"
        style={{ height: '100%', marginTop: '0.75rem' }}
      >
        <KGrid.Item xs={5}>
          <KContainer.View height="100%" position="relative">
            {isLoading && <Loading />}
            <Bar
              data={clientStatisticData}
              options={options}
              plugins={[ChartDataLabels]}
            />
          </KContainer.View>
        </KGrid.Item>
        <KGrid.Item xs={7}>
          <KContainer.View>
            <AgTable {...table} showSearch pagination />
          </KContainer.View>

          <KContainer.View row alignItems marginT={'0.75rem'}>
            <KContainer.View flex={1}>
              <KLabel.Text
                alignSelf="flex-start"
                typo="TextMdBold"
              >{`${trans('total_invoices')}: ${byClientData?.totalInvoices ?? 0}`}</KLabel.Text>
            </KContainer.View>

            <KContainer.View dp="flex" flex={1} alignItems="flex-end">
              <KLabel.Text typo="TextMdBold">
                {`${trans('total_overdue')}: `}
                <KLabel.Text color={KColors.danger.normal} typo="TextMdBold">
                  {Prototype.number.formatCurrency(
                    byClientData?.totalOverdue ?? 0
                  )}
                </KLabel.Text>
              </KLabel.Text>
            </KContainer.View>
          </KContainer.View>
        </KGrid.Item>
      </KGrid.Container>
    </KContainer.Card>
  );
};

export default memo(Client);
